import { supabase } from './client';

export async function createRally(rallyData: {
  setId: string;
  rallyNumber: number; // kept for API compatibility; actual number is computed from DB
  servingTeamId: string;
  serverId: string;
  pointWonById: string;
  lostReason: string | null;
  pointWinnerId: string | null;
  categories?: { playerId: string; category: string }[];
  isServerFault?: boolean;
}) {
  // Race-condition fix: always derive rally_number from the current DB max so that
  // concurrent scoring from two devices cannot produce duplicate rally numbers.
  const { data: existing, error: fetchErr } = await supabase
    .from('rallies')
    .select('rally_number')
    .eq('set_id', rallyData.setId)
    .order('rally_number', { ascending: false })
    .limit(1);
  if (fetchErr) throw fetchErr;
  const safeRallyNumber =
    existing && existing.length > 0 ? existing[0].rally_number + 1 : 1;

  const { data, error } = await supabase
    .from('rallies')
    .insert({
      set_id: rallyData.setId,
      rally_number: safeRallyNumber,
      serving_team_id: rallyData.servingTeamId,
      server_id: rallyData.serverId,
      point_won_by_id: rallyData.pointWonById,
      lost_reason: rallyData.lostReason,
      point_winner_id: rallyData.pointWinnerId,
      categories: rallyData.categories || [],
      is_server_fault: rallyData.isServerFault || false,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getRalliesBySet(setId: string) {
  const { data, error } = await supabase
    .from('rallies')
    .select(`
      id,
      set_id,
      rally_number,
      serving_team_id,
      server_id,
      point_won_by_id,
      lost_reason,
      point_winner_id,
      recorded_at,
      categories,
      is_server_fault
    `)
    .eq('set_id', setId)
    .order('rally_number', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getRalliesBySetForDisplay(setId: string) {
  const { data, error } = await supabase
    .from('rallies')
    .select(`
      id,
      set_id,
      rally_number,
      serving_team_id,
      server_id,
      point_won_by_id,
      lost_reason,
      point_winner_id,
      recorded_at,
      categories,
      is_server_fault
    `)
    .eq('set_id', setId)
    .order('rally_number', { ascending: true });

  if (error) throw error;
  return data;
}

export async function deleteRally(setId: string, rallyNumber: number) {
  const { error } = await supabase
    .from('rallies')
    .delete()
    .eq('set_id', setId)
    .eq('rally_number', rallyNumber);

  if (error) throw error;
}

export async function updateRallyCategories(
  rallyId: string,
  categories: { playerId: string; category: string }[]
) {
  const { data, error } = await supabase
    .from('rallies')
    .update({ categories })
    .eq('id', rallyId)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('Update blocked — no rows affected. Check RLS policies on rallies table.');
  }
}

export async function updateRallyFull(
  rallyId: string,
  fields: {
    pointWonById: string;
    servingTeamId: string;
    serverId: string;
    categories: { playerId: string; category: string }[];
    isServerFault: boolean;
  }
) {
  const { data, error } = await supabase
    .from('rallies')
    .update({
      point_won_by_id: fields.pointWonById,
      serving_team_id: fields.servingTeamId,
      server_id: fields.serverId,
      categories: fields.categories,
      is_server_fault: fields.isServerFault,
    })
    .eq('id', rallyId)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('Update blocked — no rows affected. Check RLS policies on rallies table.');
  }
}

/** Insert a new rally at the end of a set. */
export async function addRallyToSet(fields: {
  setId: string;
  servingTeamId: string;
  serverId: string;
  pointWonById: string;
  categories: { playerId: string; category: string }[];
  isServerFault: boolean;
  lostReason?: string | null;
}) {
  // Get current max rally_number for this set
  const { data: existing, error: fetchErr } = await supabase
    .from('rallies')
    .select('rally_number')
    .eq('set_id', fields.setId)
    .order('rally_number', { ascending: false })
    .limit(1);
  if (fetchErr) throw fetchErr;
  const nextNumber = existing && existing.length > 0 ? existing[0].rally_number + 1 : 1;

  const { data, error } = await supabase
    .from('rallies')
    .insert({
      set_id: fields.setId,
      rally_number: nextNumber,
      serving_team_id: fields.servingTeamId,
      server_id: fields.serverId,
      point_won_by_id: fields.pointWonById,
      lost_reason: fields.lostReason ?? null,
      point_winner_id: null,
      categories: fields.categories,
      is_server_fault: fields.isServerFault,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/** After editing a rally, recount scores from DB and patch the set row. */
export async function recomputeSetScoresFromRallies(
  setId: string,
  teamAId: string,
  teamBId: string
) {
  const { data: rallies, error } = await supabase
    .from('rallies')
    .select('point_won_by_id')
    .eq('set_id', setId);

  if (error) throw error;

  const scoreA = (rallies ?? []).filter(r => r.point_won_by_id === teamAId).length;
  const scoreB = (rallies ?? []).filter(r => r.point_won_by_id === teamBId).length;

  const { error: updateError } = await supabase
    .from('sets')
    .update({ score_a: scoreA, score_b: scoreB })
    .eq('id', setId);

  if (updateError) throw updateError;
  return { scoreA, scoreB };
}
